import { defineChannelContract } from 'shared/channels';
import type { AttachmentCounts } from 'shared/domain/issue/issueModel';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type { HashMap } from 'shared/types/commonView';
import type {
  SharedDocumentSignedUrlRequest,
  SharedResourceSignedRequestResult,
} from 'shared/download/sharedResourceSignedRequest';
import type { DocumentCreateModel } from './documentCreateModel';
import type { DocumentModel, DocumentParentQuery } from './documentModel';
import type { DocumentUrlsResult } from './documentUrlsModel';
import type { DrawingModel } from './drawingModel';
import type { DocumentEditData } from './editDocument';

/** `uploadedEntity`: a document the server stored. */
export interface UploadedDocumentPayload {
  localId: number;
  _id: string;
}

/** `createdDrawing`: a drawing saved locally on a document. */
export interface CreatedDrawingPayload {
  localId: number;
}

export interface DocumentUrlsRequest {
  localId: number;
  /** Asks the server again even when the stored document still looks local. */
  forceRefresh?: boolean;
}

export interface CountAttachmentsOfIssuesRequest {
  issueIds: string[];
}

/** The PDF-eligible attachments of each requested issue, by issue id. */
export type AttachmentCountsOfIssues = HashMap<AttachmentCounts>;

/** Document (attachment) requests answered by the service worker and the events it broadcasts. */
export const documentContract = defineChannelContract<{
  requests: {
    getOfParent: { request: DocumentParentQuery; response: DocumentModel[] };
    countAttachmentsOfIssues: { request: CountAttachmentsOfIssuesRequest; response: AttachmentCountsOfIssues };
    /** Resolves with the local id; `uniqueId` comes back with `createdEntity`. */
    create: {
      request: { baseUrl: string; document: DocumentCreateModel; uniqueId: string };
      response: number;
    };
    edit: { request: DocumentEditData; response: void };
    remove: { request: { localId: number }; response: void };
    createDrawing: { request: { drawing: DrawingModel; localId: number }; response: void };
    getUrls: { request: DocumentUrlsRequest; response: DocumentUrlsResult };
    getSignedUrlSecuredByToken: {
      request: SharedDocumentSignedUrlRequest;
      response: SharedResourceSignedRequestResult;
    };
  };
  events: {
    /** The local id of the created document. */
    [DomainMessagesTypes.createdEntity]: number[];
    /** The local id of the changed document. */
    [DomainMessagesTypes.updatedEntity]: number;
    /** The local id of the removed document. */
    [DomainMessagesTypes.deletedEntity]: number;
    [DomainMessagesTypes.uploadedEntity]: UploadedDocumentPayload;
    // A local id, or an upload error with `messageId`, depending on the worker's producer.
    /** The `localId` of the item whose upload failed, or none with an `error` of the queue. */
    [DomainMessagesTypes.failUploadedEntity]: number | undefined;
    [DomainMessagesTypes.createdDrawing]: CreatedDrawingPayload;
  };
}>('documentChannel');
